import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, MapPin, Clock, X, Newspaper, Send, Eye, TrendingUp } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimePicker from "@/components/TimePicker";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import AccountUpsell from "@/components/AccountUpsell";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import { runJob } from "@/lib/pollJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Storm-alert noise threshold (per-subscriber preference). Source of
  // truth lives on the `subscribers` row; we mirror it into local
  // component state so the radio reflects optimistic edits.
  const [alertThreshold, setAlertThreshold] = useState("standard");
  const [alertSaving, setAlertSaving] = useState(false);
  // Auto-open the "Add location" form when the dashboard sends the user
  // here with ?add=1 (empty-state CTA from /app). Avoids the previous
  // dead-end loop where the empty Manage page linked back to the landing.
  const [addOpen, setAddOpen] = useState(searchParams.get("add") === "1");
  const [newLoc, setNewLoc] = useState({ label: null, lat: null, lon: null, country: DEFAULT_COUNTRY, deliveryTime: "07:00" });
  const [editing, setEditing] = useState({});
  const [preview, setPreview] = useState({ open: false, url: "", title: "" });
  // Confirmation-dialog state for destructive actions. We use a proper
  // AlertDialog (not window.confirm) so accidental clicks on mobile get
  // a clear two-step guard. `confirmDialog.action` is the async fn to
  // run on confirm; `.kind` is 'remove-location' | 'remove-news' |
  // 'unsubscribe-all' for test-id scoping.
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, kind: "", title: "", body: "", confirmLabel: "",
    action: null,
  });
  const closeConfirm = () => setConfirmDialog((c) => ({ ...c, open: false }));
  const runConfirm = async () => {
    const fn = confirmDialog.action;
    closeConfirm();
    if (typeof fn === "function") await fn();
  };

  const openPreview = (url, title, poll = false) => setPreview({ open: true, url, title, poll });
  const closePreview = () => setPreview((p) => ({ ...p, open: false }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/manage/${token}`);
      setData(r.data);
      setAlertThreshold(r.data?.alert_threshold || "standard");
      try { localStorage.setItem("weathervane:last-token", token); } catch {}
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const saveAlertThreshold = async (next) => {
    const prev = alertThreshold;
    setAlertThreshold(next);  // optimistic
    setAlertSaving(true);
    try {
      await axios.put(`${API}/manage/${token}/preferences`, { alert_threshold: next });
      toast.success("Weather alert preference saved.");
    } catch (e) {
      setAlertThreshold(prev);  // rollback
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setAlertSaving(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const saveRow = async (sub) => {
    const patch = editing[sub.id];
    if (!patch) return;
    try {
      await axios.put(`${API}/manage/${token}/subscriptions/${sub.id}`, patch);
      toast.success("Saved.");
      setEditing((e) => { const c = { ...e }; delete c[sub.id]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteRow = (sub) => {
    // Two-step confirmation: mobile taps on a trash icon are easy to
    // trigger by accident, so we always route destructive actions
    // through the AlertDialog.
    setConfirmDialog({
      open: true,
      kind: "remove-location",
      title: "Remove this location?",
      body:
        `This will delete ${sub.location_label} from your weather ` +
        "feed. You can always add it back later. If you just want a " +
        "break, use the Pause button instead — it keeps the location " +
        "configured but stops deliveries until you flip it back on.",
      confirmLabel: "Yes, remove it",
      action: async () => {
        try {
          await axios.delete(`${API}/manage/${token}/subscriptions/${sub.id}`);
          toast.success("Removed.");
          load();
        } catch {
          toast.error("Remove failed");
        }
      },
    });
  };

  const addRow = async () => {
    if (!newLoc.lat || !newLoc.lon) {
      toast.error("Pick a validated address first.");
      return;
    }
    try {
      await axios.post(`${API}/manage/${token}/subscriptions`, {
        label: newLoc.label,
        lat: newLoc.lat,
        lon: newLoc.lon,
        country: newLoc.country || DEFAULT_COUNTRY,
        delivery_time: newLoc.deliveryTime,
      });
      toast.success("Added.");
      setAddOpen(false);
      setNewLoc({ label: null, lat: null, lon: null, country: DEFAULT_COUNTRY, deliveryTime: "07:00" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const unsubscribeAll = () => {
    setConfirmDialog({
      open: true,
      kind: "unsubscribe-all",
      title: "Unsubscribe from everything?",
      body:
        "This will turn off ALL daily forecast and news emails for " +
        "this email address. You can re-enable individual subscriptions " +
        "later from this page.",
      confirmLabel: "Yes, turn off everything",
      action: async () => {
        try {
          await axios.post(`${API}/manage/${token}/unsubscribe-all`);
          toast.success("Unsubscribed from everything.");
          load();
        } catch {
          toast.error("Unsubscribe failed");
        }
      },
    });
  };

  if (loading) {
    return <PageShell><div className="flex items-center gap-3"><Loader2 className="w-6 h-6 animate-spin" /> Loading…</div></PageShell>;
  }
  if (!data) {
    return <PageShell><div><h1 className="font-display text-3xl font-black">Link not found</h1><p className="mt-3 text-neutral-700">This manage link may have been regenerated. Check your latest email, or <Link to="/" className="underline">subscribe again</Link>.</p></div></PageShell>;
  }

  return (
    <PageShell>
      <AccountUpsell next={`/manage/${token}`} className="mb-7" />
      <div className="ticker-label mb-3" data-testid="manage-email">{data.email} {!data.verified && <span className="ml-2 px-2 py-0.5 bg-[#FFD600] text-black rounded-none text-[10px]">UNVERIFIED</span>}</div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Your subscriptions.</h1>
        {data.subscriptions.length > 0 && (
          <Link
            to={`/today/${token}`}
            data-testid="open-my-forecast-link"
            className="btn-brutal inline-flex items-center gap-2 px-4 py-3 text-xs tracking-[0.14em] uppercase font-semibold"
          >
            <Eye className="w-3 h-3" /> Open my forecast
          </Link>
        )}
      </div>
      <p className="mt-4 text-neutral-700 max-w-xl">Edit each location, change the delivery time, remove one you don&rsquo;t need, or turn off everything below.</p>

      <div className="mt-10 space-y-4" data-testid="subscriptions-list">
        {data.subscriptions.length === 0 && !addOpen && (
          <div className="border border-black p-6 text-neutral-600">
            No locations yet.{" "}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              data-testid="manage-empty-add-one"
              className="underline font-semibold hover:text-black"
            >
              Add one
            </button>
            .
          </div>
        )}
        {data.subscriptions.map((sub) => {
          const edit = editing[sub.id] || {};
          const time = edit.delivery_time ?? sub.delivery_time;
          const active = edit.active ?? sub.active;
          const dirty = Object.keys(edit).length > 0;
          return (
            <div key={sub.id} data-testid={`sub-row-${sub.id}`} className="border border-black bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 opacity-60" /> <span className="text-[15px] font-semibold">{sub.location_label}</span></div>
                  <div className="text-xs text-neutral-500">{sub.timezone}</div>
                </div>
                {!active && <span className="text-[10px] uppercase tracking-wider bg-neutral-200 px-2 py-1">Inactive</span>}
              </div>
              <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
                <div>
                  <div className="ticker-label mb-2 flex items-center gap-2"><Clock className="w-3 h-3" /> Delivery time</div>
                  <TimePicker value={time} onChange={(v) => setEditing((e) => ({ ...e, [sub.id]: { ...e[sub.id], delivery_time: v } }))} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => openPreview(`${API}/manage/${token}/subscriptions/${sub.id}/preview`, `Preview — ${sub.location_label}`)} className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-[#FFD600] inline-flex items-center gap-1" data-testid={`preview-${sub.id}`}><Eye className="w-3 h-3" /> Preview</button>
                  <button onClick={() => setEditing((e) => ({ ...e, [sub.id]: { ...e[sub.id], active: !active } }))} className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-[#FFD600]" data-testid={`toggle-active-${sub.id}`}>{active ? "Pause" : "Resume"}</button>
                  {/* Pause/Resume is the single on/off control. Snooze-1w
                      used to live here but was removed for parity with
                      the email (which no longer has a Snooze link); users
                      preferred a single "Pause" lever they can flip back
                      on whenever, rather than a time-bounded snooze. */}
                  {dirty && <button onClick={() => saveRow(sub)} className="btn-brutal px-4 py-3 text-xs uppercase tracking-wider" data-testid={`save-${sub.id}`}>Save</button>}
                  <button onClick={() => deleteRow(sub)} className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-red-50" data-testid={`delete-${sub.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!addOpen ? (
        <button onClick={() => setAddOpen(true)} className="mt-6 w-full border-2 border-dashed border-black/40 py-4 text-sm uppercase tracking-[0.14em] hover:border-black flex items-center justify-center gap-2" data-testid="open-add-location"><Plus className="w-4 h-4" /> Add another location</button>
      ) : (
        <div className="mt-6 border border-black bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="ticker-label">New location</div>
            <button onClick={() => setAddOpen(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className="ticker-label">Country</label>
            <select
              data-testid="manage-add-country"
              value={newLoc.country || DEFAULT_COUNTRY}
              onChange={(e) => setNewLoc((n) => ({ ...n, country: e.target.value, label: null, lat: null, lon: null }))}
              className="text-[11px] tracking-wider uppercase border border-black bg-white px-2 py-1 outline-none focus:bg-[#FFFBE6]"
            >
              {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
            </select>
          </div>
          <AddressAutocomplete testId="manage-add-address" country={newLoc.country || DEFAULT_COUNTRY} onSelect={(a) => setNewLoc((n) => ({ ...n, lat: a.lat, lon: a.lon, label: a.label }))} />
          <TimePicker value={newLoc.deliveryTime} onChange={(v) => setNewLoc((n) => ({ ...n, deliveryTime: v }))} />
          <button onClick={addRow} className="btn-brutal w-full py-3 text-sm uppercase tracking-[0.14em]" data-testid="add-location-submit">Add location</button>
        </div>
      )}

      {/* Weather-alert preferences. Lives between weather subscriptions
          and news subscriptions because it's a weather-class
          preference but applies to ALL of the user's locations at
          once. Default ("Standard") suppresses Special Weather
          Statements / Dense Fog Advisories / Air Quality Alerts
          while keeping Frost + Freeze advisories. "No alerts" opts
          out of alert emails + pushes entirely. */}
      <div className="mt-10 border-2 border-black bg-white p-5" data-testid="alert-preferences">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="ticker-label mb-1 text-neutral-500">Weather alerts</div>
            <h2 className="font-display text-xl font-black tracking-tight">How loud should we be?</h2>
            <p className="text-sm text-neutral-700 mt-1 max-w-xl">
              We push severe-weather alerts from the National Weather Service
              the moment they fire. Choose how strict the filter should be.
            </p>
          </div>
          {alertSaving && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
        </div>
        <div className="space-y-2.5">
          {[
            {
              value: "standard",
              label: "Standard (recommended)",
              detail: "Skip Special Weather Statements, Dense Fog, Air Quality, and similar low-signal advisories. Frost + Freeze advisories still come through.",
            },
            {
              value: "warnings",
              label: "Warnings only",
              detail: "Watches, warnings, and extreme alerts. No advisories at all.",
            },
            {
              value: "all",
              label: "Everything (verbose)",
              detail: "Every NWS alert we see, including Special Weather Statements. For power users who want it all.",
            },
            {
              value: "none",
              label: "No alerts",
              detail: "Unsubscribe from weather alerts completely. No alert emails or notifications — your daily forecast still arrives as scheduled.",
            },
          ].map((opt) => {
            const active = alertThreshold === opt.value;
            return (
              <label
                key={opt.value}
                data-testid={`alert-threshold-${opt.value}`}
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                  active ? "border-black bg-[#FFFBE6]" : "border-black/30 hover:bg-neutral-50"
                }`}
              >
                <input
                  type="radio"
                  name="alert_threshold"
                  value={opt.value}
                  checked={active}
                  onChange={() => saveAlertThreshold(opt.value)}
                  disabled={alertSaving}
                  data-testid={`alert-threshold-input-${opt.value}`}
                  className="mt-1 shrink-0"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{opt.label}</div>
                  <div className="text-xs text-neutral-600 mt-0.5 leading-snug">{opt.detail}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* News subscriptions */}
      <div className="mt-12 pt-8 border-t border-black/10">
        <div className="flex items-center gap-3 mb-4">
          <Newspaper className="w-5 h-5" />
          <h2 className="font-display text-2xl font-black tracking-tight">Weathervane News</h2>
        </div>
        {/* Helpful prompt-writing tips. Surfaces here (rather than the
            initial signup flow) because users typically only think to
            tune their prompt AFTER seeing what the digest produces.
            Folded by default so existing users who know what they're
            doing don't get a wall of text every visit. */}
        <details className="border border-black/15 bg-[#FFFBE6] mb-5" data-testid="news-prompt-tips">
          <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] hover:bg-[#FFF7CC] select-none">
            Tips for writing a great news prompt
          </summary>
          <div className="px-4 pb-4 pt-1 text-sm text-neutral-800 space-y-3 leading-relaxed">
            <p>The prompt below decides what we hunt down each morning. A tight, specific prompt almost always returns better articles than a broad one. Think of it as briefing a researcher at 6 a.m.</p>
            <p className="text-xs text-neutral-600 italic">Quotes are for example purposes only &mdash; actual prompts don&rsquo;t require quotes.</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><span className="font-semibold">Be specific about scope.</span> &ldquo;AI&rdquo; is broad. &ldquo;Practical AI tools for marketers&rdquo; or &ldquo;LLM safety research and policy&rdquo; is a brief.</li>
              <li><span className="font-semibold">Name the lens.</span> Add tone words like <em>investigative</em>, <em>long-form</em>, <em>technical</em>, <em>feel-good</em>, <em>uplifting</em>, or <em>constructive</em>. The engine adjusts its filter to match.</li>
              <li><span className="font-semibold">Stack 2&ndash;4 sub-topics.</span> &ldquo;Cybersecurity, ransomware, and CISO interviews&rdquo; pulls a richer mix than any one of those alone.</li>
              <li><span className="font-semibold">Exclude what you don&rsquo;t want.</span> &ldquo;...skip celebrity gossip and sports scores&rdquo; works.</li>
              <li><span className="font-semibold">Add a geography or industry.</span> &ldquo;European biotech IPOs&rdquo; will outperform &ldquo;biotech&rdquo; on freshness.</li>
              <li><span className="font-semibold">Iterate.</span> If a digest feels off, edit the prompt and refresh tomorrow. The engine starts over each morning &mdash; nothing carries over from yesterday&rsquo;s results.</li>
            </ul>
            <p className="text-xs text-neutral-600 italic">Example that works well: &ldquo;Practical AI tooling for product teams &mdash; favor in-depth case studies and skip funding-round announcements unless they&rsquo;re &gt;$100M.&rdquo;</p>
          </div>
        </details>
        {(!data.news_subscriptions || data.news_subscriptions.length === 0) ? (
          <div className="border border-black/30 bg-white p-5">
            <p className="text-sm text-neutral-700 mb-4">You have no news subscriptions yet.</p>
            <Link to="/news?stay=1" className="inline-flex items-center gap-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-widest">
              <Plus className="w-3 h-3" /> Subscribe to Weathervane News
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {data.news_subscriptions.map((nsub) => (
              <NewsSubRow key={nsub.id} nsub={nsub} token={token} onChange={load} onPreview={openPreview} onRequestConfirm={setConfirmDialog} />
            ))}
            <Link to="/news?stay=1" className="inline-flex items-center gap-2 mt-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-widest">
              <Plus className="w-3 h-3" /> Add another news interest
            </Link>
          </div>
        )}
      </div>

      {/* Top-10 Stocks add-on */}
      <StocksSubSection token={token} data={data} onChange={load} />

      <div className="mt-12 pt-8 border-t border-black/10">
        <button onClick={unsubscribeAll} data-testid="unsubscribe-all-button" className="px-6 py-4 text-sm uppercase tracking-[0.14em] border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors font-semibold">Unsubscribe from everything</button>
      </div>

      <EmailPreviewModal
        open={preview.open}
        onClose={closePreview}
        previewUrl={preview.url}
        poll={preview.poll}
        title={preview.title}
      />

      <AlertDialog open={confirmDialog.open} onOpenChange={(o) => !o && closeConfirm()}>
        <AlertDialogContent data-testid={`confirm-dialog-${confirmDialog.kind || "generic"}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialog.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="confirm-dialog-confirm" className="bg-red-600 hover:bg-red-700 text-white">
              {confirmDialog.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function NewsSubRow({ nsub, token, onChange, onPreview, onRequestConfirm }) {
  // Per-topic editing model (iter 31): the subscription has a `topics`
  // array of N entries. We render each as a labeled, individually-
  // editable row with delete + add controls. Master `refined_prompt`
  // is regenerated server-side from the topics on save. Single-topic
  // legacy subs (no `topics` array) get migrated transparently to a
  // 1-element array so the UI is uniform.
  const initialTopics = useMemo(() => {
    if (Array.isArray(nsub.topics) && nsub.topics.length > 0) {
      return nsub.topics.map((t, i) => ({
        text: t,
        label: (nsub.topic_labels && nsub.topic_labels[i]) || "",
      }));
    }
    return [{ text: nsub.refined_prompt || "", label: "" }];
  }, [nsub]);

  const [topics, setTopics] = useState(initialTopics);
  const [time, setTime] = useState(nsub.delivery_time);
  const [active, setActive] = useState(nsub.active);
  const [sending, setSending] = useState(false);

  const dirty =
    JSON.stringify(topics) !== JSON.stringify(initialTopics) ||
    time !== nsub.delivery_time ||
    active !== nsub.active;

  const updateTopic = (idx, text) =>
    setTopics((prev) => prev.map((t, i) => (i === idx ? { ...t, text } : t)));
  const updateLabel = (idx, label) =>
    setTopics((prev) => prev.map((t, i) => (i === idx ? { ...t, label } : t)));

  const removeTopic = (idx) => {
    if (topics.length <= 1) {
      toast.error(
        "At least one topic is required. Use the trash icon to remove the entire feed."
      );
      return;
    }
    setTopics((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTopic = () => {
    if (topics.length >= 10) {
      toast.error("Max 10 topics per feed.");
      return;
    }
    setTopics((prev) => [...prev, { text: "", label: "" }]);
  };

  const save = async () => {
    const cleaned = topics
      .map((t) => ({ text: t.text.trim(), label: t.label.trim() }))
      .filter((t) => t.text.length > 0);
    if (cleaned.length === 0) {
      toast.error("Add at least one topic before saving.");
      return;
    }
    const payload = {
      topics: cleaned.map((t) => t.text),
      topic_labels: cleaned.map((t) => t.label),
      delivery_time: time,
      active,
    };
    try {
      await axios.put(`${API}/manage/${token}/news/${nsub.id}`, payload);
      toast.success("Saved.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = () => {
    onRequestConfirm?.({
      open: true,
      kind: "remove-news",
      title: "Remove this news feed?",
      body:
        `This will stop the daily digest for all ${topics.length} topic${topics.length === 1 ? "" : "s"} in this feed. ` +
        "You can always subscribe again later.",
      confirmLabel: "Yes, remove it",
      action: async () => {
        try {
          await axios.delete(`${API}/manage/${token}/news/${nsub.id}`);
          toast.success("Removed.");
          onChange?.();
        } catch {
          toast.error("Delete failed");
        }
      },
    });
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const result = await runJob(`${API}/manage/${token}/news/${nsub.id}/send-now/start`, undefined, { timeoutMs: 240000 });
      toast.success(`Sent ${result.articles || 0} stories. Check your inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Header label — recognizable identity for this feed so a user with
  // multiple feeds knows which they're editing.
  const headerLabel = (() => {
    const labels = topics.filter((t) => t.label).map((t) => t.label);
    if (labels.length > 0) return labels.join(" · ");
    const firstWords = (topics[0]?.text || "").trim().split(/\s+/).slice(0, 6).join(" ");
    return firstWords || "News feed";
  })();

  return (
    <div data-testid={`news-sub-row-${nsub.id}`} className="border-2 border-black bg-white p-5">
      <div className="flex items-start justify-between gap-4 mb-4 pb-3 border-b border-black/10">
        <div className="flex-1 min-w-0">
          <div className="ticker-label mb-1.5 text-neutral-500">News feed</div>
          <div
            className="font-display text-lg font-black tracking-tight truncate"
            title={headerLabel}
            data-testid={`news-feed-header-${nsub.id}`}
          >
            {headerLabel}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">
            {topics.length} topic{topics.length === 1 ? "" : "s"} &middot; delivery {time}
          </div>
        </div>
        {!active && (
          <span className="text-[10px] uppercase tracking-wider bg-neutral-200 px-2 py-1 shrink-0">
            Paused
          </span>
        )}
      </div>

      {/* Per-topic editing rows */}
      <div className="space-y-3 mb-5" data-testid={`news-topics-${nsub.id}`}>
        {topics.map((t, i) => (
          <div
            key={i}
            data-testid={`news-topic-${nsub.id}-${i}`}
            className="border border-black/30 bg-[#FAFAFA] p-3"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="ticker-label !text-[10px] text-neutral-600">
                Topic {i + 1}
              </div>
              <button
                type="button"
                onClick={() => removeTopic(i)}
                disabled={topics.length <= 1}
                aria-label={`Remove topic ${i + 1}`}
                data-testid={`news-topic-remove-${nsub.id}-${i}`}
                className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-red-600 disabled:opacity-40 disabled:hover:text-neutral-500 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
            <input
              type="text"
              value={t.label}
              onChange={(e) => updateLabel(i, e.target.value)}
              placeholder="Short label (e.g. AI, Climate, Biotech)"
              data-testid={`news-topic-label-${nsub.id}-${i}`}
              className="w-full text-sm px-3 py-2 border border-black/30 bg-white outline-none focus:bg-[#FFFBE6] mb-2 font-semibold"
              maxLength={40}
            />
            <textarea
              value={t.text}
              onChange={(e) => updateTopic(i, e.target.value)}
              placeholder="Describe this topic in your own words"
              rows={2}
              data-testid={`news-topic-prompt-${nsub.id}-${i}`}
              className="w-full text-sm px-3 py-2 border border-black/30 bg-white outline-none focus:bg-[#FFFBE6]"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={addTopic}
          disabled={topics.length >= 10}
          data-testid={`news-topic-add-${nsub.id}`}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold border border-dashed border-black/40 bg-white hover:bg-[#FFD600] disabled:opacity-50"
        >
          <Plus className="w-3 h-3" /> Add another topic
        </button>
      </div>

      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end pt-3 border-t border-black/10">
        <div>
          <div className="ticker-label mb-2 flex items-center gap-2">
            <Clock className="w-3 h-3" /> Delivery time
          </div>
          <TimePicker value={time} onChange={setTime} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              onPreview?.(
                `${API}/manage/${token}/news/${nsub.id}/preview/start`,
                `Preview — News digest`,
                true
              )
            }
            className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-[#FFD600] inline-flex items-center gap-1"
            data-testid={`news-preview-${nsub.id}`}
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid={`news-send-now-${nsub.id}`}
            className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-[#FFD600] inline-flex items-center gap-1"
          >
            {sending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}{" "}
            Send now
          </button>
          <button
            onClick={() => setActive((a) => !a)}
            className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-[#FFD600]"
          >
            {active ? "Pause" : "Resume"}
          </button>
          {dirty && (
            <button
              onClick={save}
              data-testid={`news-save-${nsub.id}`}
              className="btn-brutal px-4 py-3 text-xs uppercase tracking-wider"
            >
              Save
            </button>
          )}
          <button
            onClick={del}
            aria-label="Delete entire feed"
            data-testid={`news-delete-${nsub.id}`}
            className="px-4 py-3 text-xs uppercase tracking-wider border border-black hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={45} />
            <div>
              <div className="font-display text-2xl font-black tracking-tighter leading-none">Weathervane</div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-bold mt-1">Weather &amp; News <span className="mx-1">/</span> <span className="italic">BYO Coffee</span></div>
            </div>
          </Link>
        </div>
      </header>
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16">{children}</section>
    </div>
  );
}

function StocksSubSection({ token, data, onChange }) {
  const active = !!data?.stocks_active;
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/manage/${token}/stocks-toggle`, { active: !active });
      toast.success(
        !active
          ? "You're on the Top 10 Stocks list. First digest ships tomorrow morning."
          : "Removed from the Top 10 Stocks list.",
      );
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update. Try again?");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-12 pt-8 border-t border-black/10" data-testid="manage-stocks-section">
      <div className="flex items-center gap-3 mb-4">
        <TrendingUp className="w-5 h-5" />
        <h2 className="font-display text-2xl font-black tracking-tight">Top 10 Stocks</h2>
      </div>
      <div className={`border-2 ${active ? "border-black bg-[#FFFBE6]" : "border-black/30 bg-white"} p-5`}>
        <div className="flex items-start gap-3 justify-between flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-700">Consensus watch</span>
              {active && (
                <span className="inline-block px-1.5 py-0.5 bg-black text-[#FFD600] text-[9px] uppercase tracking-[0.16em] font-bold" data-testid="manage-stocks-active-badge">
                  Subscribed
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-800 leading-relaxed">
              A daily Top-10 list ranked by how many major research desks (Goldman, Morgan Stanley, JPMorgan, BofA, Citi, UBS, Wells Fargo, Deutsche Bank, Jefferies, RBC, Barclays, Wolfe Research) currently name each ticker on their public top-picks lists. Adds and removals since yesterday are called out. Ships at the top of your daily email — or as its own morning brief if you don&rsquo;t have a news feed.
            </p>
            <p className="text-[11px] text-neutral-500 leading-snug mt-2 italic">
              Not investment advice. See the Top&nbsp;10 tab on your dashboard for the full methodology and disclaimer.
            </p>
          </div>
          <button
            onClick={toggle}
            disabled={saving}
            data-testid="manage-stocks-toggle"
            className={`px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border-2 border-black transition-colors whitespace-nowrap ${
              active ? "bg-black text-[#FFD600] hover:bg-neutral-800" : "bg-[#FFD600] hover:bg-yellow-400"
            }`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : active ? "Unsubscribe" : "Subscribe to Top 10"}
          </button>
        </div>
      </div>
    </div>
  );
}

